import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
} from '@mui/material'
import { Send, Business } from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useDepartments } from '@hooks/useDepartments'
import { departmentsApi } from '@api/departments'
import Loading from '@components/Common/Loading'

const NavigateModal = ({ open, onClose, submissionId, formId }) => {
    const { t, language } = useTranslation()
    const { navigateSubmission, isNavigating } = useDepartments()

    // Fetch departments filtered by form access if formId is provided
    const { data: departmentsData, isLoading } = useQuery({
        queryKey: ['departments', 'forForm', formId],
        queryFn: () => departmentsApi.getAll(formId),
        enabled: open && !!formId,
        staleTime: 5 * 60 * 1000,
    })

    const departments = departmentsData?.data || []

    const [selectedDepartment, setSelectedDepartment] = useState('')
    const [notes, setNotes] = useState('')

    const handleSubmit = () => {
        if (!selectedDepartment) return

        navigateSubmission({
            submissionId,
            departmentId: selectedDepartment,
            notes: notes.trim() || null,
        })

        handleClose()
    }

    const handleClose = () => {
        setSelectedDepartment('')
        setNotes('')
        onClose()
    }

    const getDepartmentName = (dept) => {
        if (language === 'ar' && dept.name_ar) {
            return dept.name_ar
        }
        return dept.name_en
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business color="primary" />
                    {t('department.navigateTo')}
                </Box>
            </DialogTitle>

            <DialogContent>
                {isLoading ? (
                    <Loading />
                ) : departments.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        {language === 'ar'
                            ? 'لا توجد أقسام لديها صلاحية الوصول إلى هذا النموذج. يرجى تعيين صلاحية الوصول للأقسام من صفحة الأقسام.'
                            : 'No departments have access to this form. Please assign form access from the Departments page.'}
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Select a department to navigate this submission to. The department users will be able to view and comment on it.
                        </Typography>

                        <FormControl fullWidth margin="normal">
                            <InputLabel>{t('department.title')}</InputLabel>
                            <Select
                                value={selectedDepartment}
                                onChange={(e) => setSelectedDepartment(e.target.value)}
                                label={t('department.title')}
                            >
                                {departments.map((dept) => (
                                    <MenuItem key={dept.id} value={dept.id}>
                                        <Box>
                                            <Typography>{getDepartmentName(dept)}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {dept.user_count} users
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Notes (optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            margin="normal"
                            multiline
                            rows={3}
                            placeholder="Add any notes or instructions for the department..."
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleClose} disabled={isNavigating}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={handleSubmit}
                    disabled={!selectedDepartment || isNavigating || departments.length === 0}
                >
                    {isNavigating ? 'Navigating...' : 'Navigate'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default NavigateModal

